import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar, Clock, User, ArrowLeft, Share2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PowerOfNature = () => {
  const navigate = useNavigate();

  const handleGoBack = () => {
    navigate('/blog');
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6">
        <div className="max-w-4xl mx-auto">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="mb-8 -ml-4 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Blog
          </Button>
          
          <div className="mb-8">
            <Badge className="mb-4">Nature Practices</Badge>
            <h1 className="text-4xl md:text-6xl font-serif font-light mb-6 text-foreground leading-tight">
              The Power of Nature – Why the Forest Heals
            </h1>
            
            <div className="flex items-center gap-6 text-sm text-muted-foreground mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>June 14, 2025</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>9 min read</span>
              </div>
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>Piotr</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4 mr-2" />
                Share Article
              </Button>
            </div>
          </div>
          
          <div className="relative overflow-hidden rounded-2xl mb-12">
            <img 
              src="/lovable-uploads/f21790ee-4570-4fde-a8c7-61222d63453d.png" 
              alt="Balanced stones in forest representing harmony with nature"
              className="w-full h-[400px] md:h-[500px] object-cover grayscale"
            />
          </div>
        </div>
      </section>

      {/* Article Content */}
      <article className="px-6 pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="prose prose-lg max-w-none">
            <div className="text-xl leading-relaxed text-muted-foreground mb-8 font-light italic">
              Picture yourself stepping into a quiet forest. The air is cooler, carrying the scent of pine and damp earth. Light filters through the leaves, and with each step, your breath slows. This isn't just a pleasant walk – it's a profound shift in your body and mind. Scientists now confirm what humans have known for centuries: nature heals.
            </div>
            
            <div className="space-y-8 text-lg leading-relaxed text-foreground">
              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">The science of nature's impact</h2>

              <p className="mb-6 leading-relaxed">
                Modern life exposes us to a constant stream of stressors – noise, deadlines, screens. Our nervous systems remain on high alert. Studies show that spending time in natural environments lowers cortisol (the stress hormone), reduces blood pressure, and improves mood (Ulrich et al., 1991). A landmark study from the University of Exeter found that people who spend at least <strong className="text-accent">two hours per week in nature report significantly better health and well-being</strong> (White et al., 2019).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Forest bathing – Shinrin-yoku</h2>

              <p className="mb-6 leading-relaxed">
                In Japan, the practice of <em>shinrin-yoku</em>, or "forest bathing," has been integrated into preventive healthcare. It involves immersing oneself in the forest atmosphere – walking slowly, breathing deeply, noticing the details of the environment. Research published in <em>Frontiers in Psychology</em> indicates that participants in forest bathing experience <strong className="text-accent">improved immune function, reduced anxiety, and increased vitality</strong> (Hansen et al., 2017).
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Why forests matter</h2>

              <p className="mb-6 leading-relaxed">
                Forests are often called the "lungs of the Earth." Beyond their ecological role, they provide psychological refuge. Attention Restoration Theory (Kaplan & Kaplan, 1989) suggests that natural environments restore our capacity for focus by engaging our attention effortlessly. <strong className="text-accent">That's why a simple walk among trees can leave us feeling refreshed and more creative</strong>.
              </p>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Practical ways to reconnect</h2>

              <p className="mb-6 leading-relaxed">
                Even if you don't live near vast forests, you can still reap the benefits of nature:
              </p>

              <ul className="space-y-3 ml-6 mb-6">
                <li><strong className="text-accent">Take daily walks</strong> in green spaces like parks or gardens.</li>
                <li><strong className="text-accent">Bring nature indoors</strong> with plants, natural light, and fresh air.</li>
                <li><strong className="text-accent">Practice mindful observation</strong> outdoors – notice textures, sounds, and scents.</li>
                <li><strong className="text-accent">Schedule regular time in nature</strong> as a non-negotiable part of self-care.</li>
              </ul>

              <div className="h-px bg-gradient-to-r from-transparent via-border to-transparent my-12"></div>

              <h2 className="text-3xl font-serif font-light mb-12 mt-12 text-foreground">Conclusion</h2>

              <p className="mb-6 leading-relaxed">
                The forest is more than scenery. It is medicine for the mind, body, and spirit. Whether through long hikes, quiet moments under trees, or simple daily contact with greenery, reconnecting with nature is one of the most powerful ways to restore balance in modern life.
              </p>

              <p className="text-xl font-light italic text-accent mb-6">
                In nature's embrace, we remember who we truly are.
              </p>

              <div className="bg-card p-8 rounded-2xl mt-16 border border-border/20">
                <h3 className="text-2xl font-serif font-light mb-4 text-foreground">References</h3>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <p>• Ulrich, R. S., Simons, R. F., Losito, B. D., Fiorito, E., Miles, M. A., & Zelson, M. (1991). Stress recovery during exposure to natural and urban environments. <em>Journal of Environmental Psychology, 11</em>(3), 201–230.</p>
                  <p>• White, M. P., Alcock, I., Grellier, J., Wheeler, B. W., Hartig, T., Warber, S. L., ... & Fleming, L. E. (2019). Spending at least 120 minutes a week in nature is associated with good health and wellbeing. <em>Scientific Reports, 9</em>(1), 7730.</p>
                  <p>• Hansen, M. M., Jones, R., & Tocchini, K. (2017). Shinrin-yoku (forest bathing) and nature therapy: A state-of-the-art review. <em>International Journal of Environmental Research and Public Health, 14</em>(8), 851.</p>
                  <p>• Kaplan, R., & Kaplan, S. (1989). <em>The Experience of Nature: A Psychological Perspective</em>. Cambridge University Press.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default PowerOfNature;